import type { ISODateString } from '../../types';

/**
 * ============================================================================
 * DATE HELPERS — calculation engine support (Phase 2)
 * ============================================================================
 *
 * All dates in this app are ISO date strings ("YYYY-MM-DD"), never Date
 * objects with a time component (see src/types/common.ts's documented
 * convention). These helpers all operate on that string representation,
 * using UTC internally ONLY as a neutral arithmetic space (never to
 * imply a timezone-aware instant) so that date math is never off-by-one
 * due to the host browser's local timezone.
 */

/** Parses "YYYY-MM-DD" into a UTC-midnight Date used purely for arithmetic. */
function toUtcDate(iso: ISODateString): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

/** Formats a UTC-midnight Date back to "YYYY-MM-DD". */
function fromUtcDate(date: Date): ISODateString {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** True if a < b (lexicographic comparison is valid for well-formed ISO dates, but this is explicit and readable at call sites). */
export function isBefore(a: ISODateString, b: ISODateString): boolean {
  return a < b;
}

export function isBeforeOrEqual(a: ISODateString, b: ISODateString): boolean {
  return a <= b;
}

export function isAfter(a: ISODateString, b: ISODateString): boolean {
  return a > b;
}

export function isAfterOrEqual(a: ISODateString, b: ISODateString): boolean {
  return a >= b;
}

/** Number of whole calendar days from `start` to `end` (end - start). Negative if end is before start. */
export function daysBetween(start: ISODateString, end: ISODateString): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((toUtcDate(end).getTime() - toUtcDate(start).getTime()) / msPerDay);
}

/** Adds `days` calendar days to an ISO date (negative to subtract). */
export function addDays(iso: ISODateString, days: number): ISODateString {
  const d = toUtcDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtcDate(d);
}

/**
 * Adds `months` calendar months to an ISO date, clamping the day-of-month
 * to the target month's actual last day if it would otherwise overflow
 * (e.g. 31-Jan + 1 month = 28/29-Feb, never "3-Mar"). This clamping
 * matters for EMI-day-of-month arithmetic (see nextEmiDate below), since
 * an EMI day fixed at, say, 31 would otherwise silently roll into the
 * next month on shorter months.
 */
export function addMonthsClamped(iso: ISODateString, months: number): ISODateString {
  const d = toUtcDate(iso);
  const targetMonthIndex = d.getUTCMonth() + months;
  const originalDay = d.getUTCDate();
  // Setting date to 1 first avoids JS's own overflow rollover interfering
  // with our own explicit clamping below.
  const probe = new Date(Date.UTC(d.getUTCFullYear(), targetMonthIndex, 1));
  const lastDayOfTargetMonth = new Date(Date.UTC(probe.getUTCFullYear(), probe.getUTCMonth() + 1, 0)).getUTCDate();
  probe.setUTCDate(Math.min(originalDay, lastDayOfTargetMonth));
  return fromUtcDate(probe);
}

/** Whole number of days in the calendar year containing `iso` (365 or 366). Used only by the 'actual_365_25'/actual-year conventions if ever needed; the default 'actual_365_fixed' convention ignores this and always divides by 365. */
export function daysInYearOf(iso: ISODateString): number {
  const year = toUtcDate(iso).getUTCFullYear();
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return isLeap ? 366 : 365;
}

/**
 * Given a loan's EMI day-of-month, returns the date of the FIRST EMI
 * cycle's closing/settlement date, STRICTLY AFTER `fromDate` (never
 * equal to it — a zero-day cycle, where a cycle both starts and closes
 * on the same calendar day, is never meaningful and would produce a
 * spurious zero-interest EMI cycle if allowed). If `fromDate`'s
 * day-of-month is before `emiDayOfMonth`, the first closing date is
 * later in the same month; otherwise (on or after `emiDayOfMonth`,
 * including exactly on it) it is `emiDayOfMonth` of the FOLLOWING month.
 */
export function firstEmiDateStrictlyAfter(fromDate: ISODateString, emiDayOfMonth: number): ISODateString {
  const d = toUtcDate(fromDate);
  const candidateThisMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), emiDayOfMonth));
  if (candidateThisMonth.getTime() > d.getTime()) {
    return fromUtcDate(candidateThisMonth);
  }
  return addMonthsClamped(fromUtcDate(candidateThisMonth), 1);
}

/** The next EMI date, exactly one cycle (one calendar month) after `emiDate`. */
export function nextEmiDate(emiDate: ISODateString): ISODateString {
  return addMonthsClamped(emiDate, 1);
}

/**
 * The Indian financial year label ("2026-27") that a given date falls
 * into. FY runs 1-April to 31-March: a date in Jan–Mar belongs to the FY
 * that STARTED the previous calendar April.
 */
export function financialYearOf(iso: ISODateString): string {
  const d = toUtcDate(iso);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1; // 1-12
  const startYear = month >= 4 ? year : year - 1;
  const endYearShort = String((startYear + 1) % 100).padStart(2, '0');
  return `${startYear}-${endYearShort}`;
}

export function todayIso(): ISODateString {
  const now = new Date();
  return fromUtcDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

/**
 * Whole calendar months from `start` to `end` ((end.year - start.year) * 12
 * + (end.month - start.month)), ignoring the day of month. Used only for
 * counting instalments between two EMI due dates, which always share the
 * same EMI day. Negative if `end` is in an earlier month than `start`.
 */
export function wholeMonthsBetween(start: ISODateString, end: ISODateString): number {
  const a = toUtcDate(start);
  const b = toUtcDate(end);
  return (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
}
